import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetAllUsersDataQuery } from "../../RTKQuery/CsvDataService/csvDataApi";
import Loader from "../Loader/Loader";
import PageComponentHeader from "../PageComponentHeader";
import FilterDrawer from "../UIKit/Drawer";
import ConditionalFilters from "../ConditionalFilters";
import { useLazyExportUsersQuery } from "../../RTKQuery/CsvDataService/csvDataApi";
import DataTable from "../UIKit/CollapsibleTable";
import { dynamicUserColumns } from "../../constants/componentConstants";
import Upload from "../Modals/Upload/Upload";
import MappingModal from "../../pages/Dashboard/components/MappingModal/MappingModal";
import {
  Button,
  Box,
  IconButton,
  Typography,
  Paper,
  Chip,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import SaveIcon from "@mui/icons-material/Save";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ExportWithOptionsButton from "../UIKit/Buttons/ExportWithOptions/index";
import { searchField, searchInput } from "../../pages/Dashboard/styles";
import {
  columnsButton,
  tableContainer,
  campaignActionsSection,
  headerRow,
  actionsRow,
  versionsSection,
  versionsTitle,
  versionsContainer,
  filterVersionChip,
  suppressionVersionChip,
  exportVersionChip,
  latestFilterVersionChip,
  latestSuppressionVersionChip,
  latestExportVersionChip,
} from "./styles";
import { useColumnVisibility } from "./hooks/useColumnVisibility";
import {
  createCenteredColumn,
  createTierColumn,
  createDynamicColumns,
  renderPercentageCell,
  renderDefaultCell,
} from "./utils/columnHelpers";
import ColumnVisibilityMenu from "./components/ColumnVisibilityMenu";
import {
  useGetCampaignVersionHistoryQuery,
  useSaveCampaignDraftMutation,
  useUploadSuppressionCsvMutation,
} from "../../RTKQuery/CampaignService/campaignApi";

export default function UsersList({ filters, setFilters }) {
  const PROSPECTS_COLUMN_ORDER_STORAGE_KEY = "prospects-table-column-order";
  const location = useLocation();
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const [sorting, setSorting] = useState([]);
  const [usersData, setUsersData] = useState([]);
  // Filters
  const [closeFilterMenu, setCloseFilterMenu] = useState(true);
  const [filterCount, setFilterCount] = useState(1);
  const [conditionCount, setConditionCount] = useState(0);
  const [subConditionCount, setSubConditionCount] = useState(0);
  const [saveFilterSelection, setSaveFilterSelection] = useState(false);
  const [filter, setFilter] = React.useState("");
  const [originalFilters, setOriginalFilters] = useState([]);

  const [loader, setLoader] = useState(true);
  const [filterLoader, setFilterLoader] = useState(false);
  const [triggerExport, { isLoading }] = useLazyExportUsersQuery();

  const [uploadModal, setUploadModal] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [mapModal, setMapModal] = useState(false);
  const [uploadTypeApollo, setUploadTypeApollo] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isSuppressionUpload, setIsSuppressionUpload] = useState(false);

  //Search Props
  const [inputValue, setInputValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [exportFilters, setExportFilters] = useState([]);
  const [statsFilterType, setStatsFilterType] = useState("All");
  const [appliedFilterType, setAppliedFilterType] = useState("All");
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [suppressedUserIds, setSuppressedUserIds] = useState([]);
  const [versionType, setVersionType] = useState(null); // "filter" | "suppress" | null

  // Get campaignId from URL params
  const searchParams = new URLSearchParams(location.search);
  const campaignIdFromUrl = searchParams.get("campaignId");

  // Get campaign version history - fetch immediately when campaignId is in URL
  const { data: versionHistoryData, refetch: refetchVersionHistory } =
    useGetCampaignVersionHistoryQuery(campaignIdFromUrl, {
      skip: !campaignIdFromUrl,
    });

  // Sort versions by createdAt (oldest first) to display in ascending order
  const versionHistory = useMemo(() => {
    const versions = versionHistoryData?.versions || versionHistoryData || [];
    return [...versions].sort((a, b) => {
      const dateA = new Date(a.createdAt || a.created_at || 0);
      const dateB = new Date(b.createdAt || b.created_at || 0);
      return dateA - dateB; // Ascending order (oldest first)
    });
  }, [versionHistoryData]);

  // Get latest filter version's filter conditions
  const latestFilterVersion = useMemo(() => {
    if (versionHistory.length > 0) {
      const filterVersions = versionHistory.filter((v) => {
        const type = (v.type || v.versionType || "").toLowerCase();
        return (
          (type === "filter" || type === "filtered") &&
          v.filterConditions &&
          Array.isArray(v.filterConditions) &&
          v.filterConditions.length > 0
        );
      });
      if (filterVersions.length > 0) {
        return filterVersions[filterVersions.length - 1];
      }
    }
    return null;
  }, [versionHistory]);

  // Save campaign draft mutation
  const [saveCampaignDraft, { isLoading: savingDraft }] =
    useSaveCampaignDraftMutation();

  // Upload suppression CSV mutation
  const [uploadSuppressionCsv, { isLoading: uploadingSuppression }] =
    useUploadSuppressionCsvMutation();

  //Alert props
  const [showAlert, setShowAlert] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("error");

  const defaultFilter = [
    {
      id: "f-1",
      first: true,
      conditions: [
        {
          id: "f-10",
          first: true,
          subConditions: [],
          filter: "f-1",
        },
      ],
    },
  ];
  const [filterData, setFilterData] = useState(defaultFilter);

  // Load latest filter version's filter conditions when available and apply them automatically
  useEffect(() => {
    if (
      campaignIdFromUrl &&
      latestFilterVersion &&
      latestFilterVersion.filterConditions &&
      Array.isArray(latestFilterVersion.filterConditions) &&
      latestFilterVersion.filterConditions.length > 0
    ) {

      setFilterData(latestFilterVersion.filterConditions);
      setFilters(latestFilterVersion.filterConditions);
      setExportFilters(latestFilterVersion.filterConditions);

      const filterTypeValue = latestFilterVersion.filterType;
      if (filterTypeValue) {
        setAppliedFilterType(filterTypeValue);
        setStatsFilterType(filterTypeValue);
      }
      setFiltersApplied(true);
    } else if (!campaignIdFromUrl) {
      setFilterData(defaultFilter);
      setFilters([]);
      setExportFilters([]);
      setFiltersApplied(false);
    }
  }, [campaignIdFromUrl, latestFilterVersion]);

  // Applying Filters
  const {
    data,
    isLoading: dataLoading,
    isFetching,
    refetch,
  } = useGetAllUsersDataQuery(
    {
      pagination,
      sorting,
      filters: JSON.stringify(filters),
      searchQuery,
      filterType: appliedFilterType === "All" ? undefined : appliedFilterType,
      campaignId: campaignIdFromUrl || undefined,
      suppressedUserIds:
        suppressedUserIds.length > 0 ? suppressedUserIds : undefined,
      versionType: versionType || undefined,
    },
    {
      // Force refetch when versionType is set to ensure backend receives the request
      // This prevents RTK Query from using cached data when filters are the same
      refetchOnMountOrArgChange: versionType ? true : false,
    }
  );

  const handleApplyFilters = () => {
    setFilterLoader(true);
    setPagination({ ...pagination, pageIndex: 0 });
    setFilters(filterData);
    setExportFilters(filterData);
    setAppliedFilterType(statsFilterType);
    setCloseFilterMenu(true);

    if (campaignIdFromUrl) {
      setFiltersApplied(true);
      setVersionType("filter");
    } else {
      setFiltersApplied(false);
      setVersionType(null);
    }
  };

  const handleResetFilters = () => {
    // For list generation view (campaignId exists), only clear the drawer filters
    // if (campaignIdFromUrl) {
    //   setFilterData([
    //     {
    //       id: "f-1",
    //       first: true,
    //       conditions: [
    //         {
    //           id: "f-10",
    //           first: true,
    //           subConditions: [],
    //           filter: "f-1",
    //         },
    //       ],
    //     },
    //   ]);
    //   setFilter("");
    //   setSaveFilterSelection(false);
    //   setOriginalFilters([]);
    //   // setCloseFilterMenu(true);
    //   return;
    // }

    // Normal view: Clear filters and refetch data
    setFilterLoader(true);
    setPagination({ ...pagination, pageIndex: 0 });
    setExportFilters([]);
    setFilter("");
    setSaveFilterSelection(false);
    setOriginalFilters([]);
    setStatsFilterType("All");
    setAppliedFilterType("All");
    setFiltersApplied(false);
    setSuppressedUserIds([]);
    setVersionType(null);
    setFilterData([
      {
        id: "f-1",
        first: true,
        conditions: [
          {
            id: "f-10",
            first: true,
            subConditions: [],
            filter: "f-1",
          },
        ],
      },
    ]);
    setFilters([]);
    if (campaignIdFromUrl) {
      setCloseFilterMenu(false);
    } else {
      setCloseFilterMenu(true);
    }
  };
  const handleExport = async () => {
    try {
      await triggerExport({
        filters,
        filterType: appliedFilterType === "All" ? undefined : appliedFilterType,
      }).unwrap();
    } catch (error) {
      console.error("Error exporting users:", error);
    }
  };

  const handleUploadModal = (bool) => {
    setShowMap(bool);
    setUploadModal(true);
  };

  const handleUploadUsers = () => {
    setUploadTypeApollo(false);
    handleUploadModal(true);
  };

  const handleSaveDraft = async () => {
    if (!campaignIdFromUrl) return;
    try {
      await saveCampaignDraft({
        campaignId: campaignIdFromUrl,
      }).unwrap();
      refetchVersionHistory();
    } catch (error) {
      console.error("Error saving draft:", error);
    }
  };

  const handleSuppressionUpload = () => {
    if (!campaignIdFromUrl) return;
    setIsSuppressionUpload(true);
    setUploadTypeApollo(false);
    setShowMap(false);
    setUploadModal(true);
  };

  const handleSuppressionFileUpload = async (file) => {
    if (!campaignIdFromUrl || !file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("campaignId", campaignIdFromUrl);
      const response = await uploadSuppressionCsv({
        campaignId: campaignIdFromUrl,
        formData: formData,
      }).unwrap();

      setVersionType("suppress");

      if (
        response?.suppressedUserIds &&
        Array.isArray(response.suppressedUserIds)
      ) {
        setSuppressedUserIds((prev) => {
          const newIds = response.suppressedUserIds
            .map((id) => Number(id))
            .filter((id) => !prev.includes(id));
          return [...prev, ...newIds];
        });
      }

      refetchVersionHistory();
      refetch();
      setUploadModal(false);
      setIsSuppressionUpload(false);
    } catch (error) {
      console.error("Error uploading suppression CSV:", error);
      setIsSuppressionUpload(false);
    }
  };

  const userListColumns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: "email",
        header: "Email",
        enableSorting: false,
      },
      {
        accessorKey: "firstName",
        header: "First Name",
        enableSorting: false,
        Cell: renderDefaultCell,
      },
      {
        accessorKey: "lastName",
        header: "Last Name",
        enableSorting: false,
        Cell: renderDefaultCell,
      },
      createTierColumn("tierNumber", "Tier Number"),
      createTierColumn("tierType", "Tier Type"),
      createTierColumn("tierLevel", "Tier Level"),
      createCenteredColumn("totalCampaigns", "Campaigns"),
      createCenteredColumn("totalSent", "Delivered"),
      createCenteredColumn("totalOpens", "Opens"),
      createCenteredColumn("totalClicks", "Clicks"),
      createCenteredColumn("totalUnsubScribed", "Unsubscribed"),
      createCenteredColumn("openRate", "Open Rate", 80, renderPercentageCell),
    ];

    const existingKeys = new Set(baseColumns.map((col) => col.accessorKey));
    const dynamicColumns = createDynamicColumns(
      dynamicUserColumns,
      existingKeys
    );

    return [...baseColumns, ...dynamicColumns];
  }, []);

  const defaultColumnOrder = useMemo(() => {
    return userListColumns
      .map((column) => column.id || column.accessorKey)
      .filter(Boolean);
  }, [userListColumns]);

  const [columnOrder, setColumnOrder] = useState(() => {
    try {
      const storedValue = localStorage.getItem(
        PROSPECTS_COLUMN_ORDER_STORAGE_KEY
      );
      const parsedValue = storedValue ? JSON.parse(storedValue) : [];
      return Array.isArray(parsedValue) ? parsedValue : [];
    } catch (error) {
      return [];
    }
  });

  useEffect(() => {
    if (!defaultColumnOrder.length) return;

    setColumnOrder((previousOrder) => {
      const safePreviousOrder = Array.isArray(previousOrder)
        ? previousOrder.filter((columnId) => defaultColumnOrder.includes(columnId))
        : [];
      const missingColumns = defaultColumnOrder.filter(
        (columnId) => !safePreviousOrder.includes(columnId)
      );
      return [...safePreviousOrder, ...missingColumns];
    });
  }, [defaultColumnOrder]);

  useEffect(() => {
    if (!columnOrder.length) return;
    localStorage.setItem(
      PROSPECTS_COLUMN_ORDER_STORAGE_KEY,
      JSON.stringify(columnOrder)
    );
  }, [columnOrder]);

  const {
    columnVisibility,
    setColumnVisibility,
    columnMenuAnchor,
    columnSearchQuery,
    setColumnSearchQuery,
    handleColumnMenuOpen,
    handleColumnMenuClose,
    handleToggleColumn,
  } = useColumnVisibility(userListColumns);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchQuery(inputValue);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [inputValue]);

  const handleSearchChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleSearch = () => {
    setSearchQuery(inputValue);
  };

  useEffect(() => {
    // refetch()
    if (data) {
      setLoader(false);
      setUsersData(data.users);
      if (versionType) {
        setVersionType(null);
      }
    }
  }, [data, usersData, dataLoading, versionType]);

  // Clear filter loader once the filtered request finishes
  useEffect(() => {
    if (filterLoader && !isFetching && !dataLoading) {
      setFilterLoader(false);
    }
  }, [filterLoader, isFetching, dataLoading]);

  useEffect(() => {
    if (!filterLoader) return;

    const timeout = setTimeout(() => {
      setFilterLoader(false);
    }, 10000);

    return () => clearTimeout(timeout);
  }, [filterLoader]);

  useEffect(() => {
    if (campaignIdFromUrl && filtersApplied && data && !isFetching) {
      refetchVersionHistory();
    }
  }, [
    campaignIdFromUrl,
    filtersApplied,
    data,
    isFetching,
    refetchVersionHistory,
  ]);

  useEffect(() => {
    if (!campaignIdFromUrl) {
      setFiltersApplied(false);
      setSuppressedUserIds([]);
      setVersionType(null);
    }
  }, [campaignIdFromUrl]);

  return (
    <div>
      <PageComponentHeader allowBack={false} pageTitle={"Prospects Management"}>
        {!campaignIdFromUrl && (
          <Box sx={searchField}>
            <input
              type="text"
              style={searchInput}
              className="inputSearch"
              placeholder="Search by Email..."
              onChange={handleSearchChange}
            />
            <IconButton onClick={handleSearch}>
              <SearchIcon style={{ color: "#367598" }} />
            </IconButton>
          </Box>
        )}
        <Button
          startIcon={<UploadFileIcon />}
          color="primary"
          variant="contained"
          sx={{
            color: "#F3F3F3",
            display: campaignIdFromUrl && filtersApplied ? "none" : "flex",
          }}
          onClick={handleUploadUsers}
        >
          Upload CSV
        </Button>
        {campaignIdFromUrl && (
          <ExportWithOptionsButton
            filters={exportFilters}
            sorting={sorting}
            total={data?.total}
            filterType={
              appliedFilterType === "All" ? undefined : appliedFilterType
            }
            campaignId={campaignIdFromUrl || undefined}
            suppressedUserIds={
              suppressedUserIds.length > 0 ? suppressedUserIds : undefined
            }
            onExportComplete={() => {
              refetchVersionHistory();
              if (campaignIdFromUrl) {
                handleResetFilters();
                navigate("/prospects", { replace: true });
              }
            }}
          />
        )}
        <Button
          startIcon={<ViewColumnIcon />}
          color="primary"
          variant="outlined"
          onClick={handleColumnMenuOpen}
          sx={columnsButton}
        >
          Columns
        </Button>
        <ColumnVisibilityMenu
          anchorEl={columnMenuAnchor}
          open={Boolean(columnMenuAnchor)}
          onClose={handleColumnMenuClose}
          columns={userListColumns}
          columnVisibility={columnVisibility}
          columnSearchQuery={columnSearchQuery}
          onSearchChange={setColumnSearchQuery}
          onToggleColumn={handleToggleColumn}
        />
        <FilterDrawer
          close={closeFilterMenu}
          setClose={setCloseFilterMenu}
          filters={filters}
          setCloseFilterMenu={setCloseFilterMenu}
        >
          <>
            <ConditionalFilters
              onReset={handleResetFilters}
              onSubmit={handleApplyFilters}
              filtersArray={filterData}
              setFiltersArray={setFilterData}
              setCloseFilterMenu={setCloseFilterMenu}
              filterCount={filterCount}
              setFilterCount={setFilterCount}
              conditionCount={conditionCount}
              setConditionCount={setConditionCount}
              subConditionCount={subConditionCount}
              setSubConditionCount={setSubConditionCount}
              defaultFilter={defaultFilter}
              saveFilterSelection={saveFilterSelection}
              setSaveFilterSelection={setSaveFilterSelection}
              filter={filter}
              setFilter={setFilter}
              originalFilters={originalFilters}
              setOriginalFilters={setOriginalFilters}
              filterType={"user"}
              statsFilterType={statsFilterType}
              setStatsFilterType={setStatsFilterType}
              hideResetButton={false}
            />
          </>
        </FilterDrawer>
      </PageComponentHeader>

      {campaignIdFromUrl && (
        <Box sx={campaignActionsSection}>
          <Box sx={headerRow}>
            <Typography sx={versionsTitle}>List Versions</Typography>

            <Box sx={actionsRow}>
              {/* {versionHistory.length > 0 && (
                <Button
                  startIcon={<SaveIcon />}
                  color="primary"
                  variant="contained"
                  size="small"
                  sx={{ color: "#F3F3F3", textTransform: "none" }}
                  onClick={handleSaveDraft}
                  disabled={savingDraft}
                >
                  {savingDraft ? "Saving..." : "Save Draft"}
                </Button>
              )} */}
              <Button
                startIcon={<CloudUploadIcon />}
                color="primary"
                variant="outlined"
                size="small"
                sx={{
                  borderColor: "#367598",
                  color: "#367598",
                  textTransform: "none",
                }}
                onClick={handleSuppressionUpload}
              >
                Upload Suppression CSV
              </Button>
            </Box>
          </Box>

          {/* Version History */}
          {versionHistory.length > 0 && (
            <Box sx={versionsContainer}>
              {versionHistory.map((version, index) => {
                const versionType =
                  version.type || version.versionType || "filter";
                const versionNumber =
                  version.version || version.versionNumber || "";
                const versionName = version.name || version.versionName || "";
                // Latest is the last item after sorting by createdAt (oldest first, newest last)
                const isLatest = index === versionHistory.length - 1;

                let chipStyle = filterVersionChip;
                let displayLabel = versionName;

                if (versionType === "filter" || versionType === "Filtered") {
                  chipStyle = isLatest
                    ? latestFilterVersionChip
                    : filterVersionChip;
                  if (!versionName) {
                    displayLabel = `Filtered list ${versionNumber || `V${index + 1}.0`
                      }`;
                  }
                } else if (
                  versionType === "suppression" ||
                  versionType === "Suppressed"
                ) {
                  chipStyle = isLatest
                    ? latestSuppressionVersionChip
                    : suppressionVersionChip;
                  if (!versionName) {
                    displayLabel = `Suppressed list ${versionNumber || `V${index + 1}.0`
                      }`;
                  }
                } else if (
                  versionType === "export" ||
                  versionType === "Exported"
                ) {
                  chipStyle = isLatest
                    ? latestExportVersionChip
                    : exportVersionChip;
                  if (!versionName) {
                    displayLabel = `Exported list ${versionNumber || `V${index + 1}.0`
                      }`;
                  }
                }

                return (
                  <Chip
                    key={version.id || index}
                    label={displayLabel}
                    sx={chipStyle}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {dataLoading || filterLoader ? (
        <div className="noData">
          <Loader />
        </div>
      ) : data?.users.length === 0 ? (
        <h3 className="noData">No Record Found</h3>
      ) : !loader && data?.users.length > 0 ? (
        <div style={{ marginTop: "1rem" }}>
          <DataTable
            columns={userListColumns}
            data={usersData}
            totalData={data.total}
            sorting={sorting}
            setSorting={setSorting}
            pagination={pagination}
            setLoader={setLoader}
            showLoaderOnSort={false}
            setPagination={setPagination}
            columnVisibility={columnVisibility}
            setColumnVisibility={setColumnVisibility}
            columnOrder={columnOrder}
            setColumnOrder={setColumnOrder}
          />
        </div>
      ) : (
        <div className="noData">
          <Loader />
        </div>
      )}
      <Upload
        open={uploadModal}
        setOpen={(open) => {
          setUploadModal(open);
          if (!open) {
            setIsSuppressionUpload(false);
          }
        }}
        setMapModal={setMapModal}
        showMap={showMap}
        setShowMap={setShowMap}
        uploadTypeApollo={uploadTypeApollo}
        BtnText={uploadTypeApollo ? "Upload Apollo Csv" : "Upload Csv"}
        uploading={uploading}
        setUploading={setUploading}
        refetchData={refetch}
        isSuppressionUpload={isSuppressionUpload}
        campaignId={campaignIdFromUrl}
        onSuppressionUpload={handleSuppressionFileUpload}
      />
      <MappingModal
        open={mapModal}
        setOpen={setMapModal}
        uploadTypeApollo={uploadTypeApollo}
        setUploading={setUploading}
        setUploadModal={setUploadModal}
        setShowAlert={setShowAlert}
        setSeverity={setSeverity}
        setMessage={setMessage}
      />
    </div>
  );
}
